
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { UserRepoInterface } from '@/services/Users/Interfaces';
import DepartmentRepositoryInterface, { DepartmentInterface } from '@/services/Departments/Interfaces';
import { FilterRequestData } from '@/services/QC-V2/chat/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';

interface FilterItemInterface {
  text: string,
  value: string,
}

enum FilterType {
  MENU,
  AUTOCOMPLETE,
  INPUT,
  COMBOBOX,
}

interface FilterInterface {
  text: string,
  value: string,
  icon: string,
  type: FilterType,
  items: FilterItemInterface[],
  selected: FilterItemInterface,
}

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'userRepository',
    'departmentRepository',
    'tagRepositoryV2',
  ],
})
export default class FeedbacksFilters extends Vue {
  private userRepository!: UserRepoInterface;
  private departmentRepository!: DepartmentRepositoryInterface;
  private tagRepositoryV2!: TagRepositoryInterface;

  private departments: DepartmentInterface[] = [];

  private tags: TagInterface[] = [];

  private selectedFilters: FilterInterface[] = [];

  private filters: FilterInterface[] = [];

  private customDates: string[] = [];

  private keepCustomPeriodActive(filter: FilterInterface, index: number): void {
    setTimeout(() => {
      if (filter.value === 'feedback.timestamp' && filter.selected && filter.selected.value === 'custom period') {
        filter.selected = { text: 'CUSTOM PERIOD', value: 'custom period' };
        this.selectedFilters[index] = filter;
      }
    }, 10);
  }

  private get datesRangeText(): string {
    return this.customDates.join(' ~ ');
  }

  private async created(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      this.departments = await this.departmentRepository.getAllDepartments();
      this.addChatIdFilter();
      this.addTicketNumberFilter();
      await this.getSupportAgents();
      await this.getQAStaff();
      this.addDatesFilter();
      this.addRatesFilter();
      this.addStatusFilter();
      await this.addTagsFilter();
      this.selectedFilters = this.filters;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getSupportAgents(): Promise<void> {
    if (!this.$store.getters.scopes.includes('qa:manager')) {
      return;
    }

    const supportDepartment = this.departments.find((department) => department.name.toLowerCase() === 'support');
    if (!supportDepartment) {
      this.$errorDialog.show('\'Support\' Department not found...');
      return;
    }

    const supportAgents = await this.userRepository.getUsersByDepartment(supportDepartment._id);
    this.filters.push(
      {
        text: 'Agent',
        value: 'feedback.feedbackTo',
        icon: 'mdi-account',
        type: FilterType.AUTOCOMPLETE,
        items: supportAgents.map((agent) => ({ text: agent.email, value: agent.id })),
        selected: { text: '', value: '' },
      },
    );
  }

  private async getQAStaff(): Promise<void> {
    const qaDepartment = await this.departments.find((department) => department.name === 'Quality Control')
    if (!qaDepartment) {
      this.$errorDialog.show('\'Quality Control\' Department not found...');
      return;
    }

    const staff = await this.userRepository.getUsersByDepartment(qaDepartment._id);
    this.filters.push(
      {
        text: 'Reviewer',
        value: 'feedback.feedbackFrom',
        icon: 'mdi-account-tie',
        type: FilterType.AUTOCOMPLETE,
        items: staff.map((agent) => ({ text: agent.email, value: agent.id })),
        selected: { text: '', value: '' },
      },
    );
  }

  private addChatIdFilter(): void {
    const filter: FilterInterface = {
      text: 'Chat ID',
      value: 'chat.id',
      icon: 'mdi-pound-box',
      type: FilterType.INPUT,
      items: [],
      selected: { text: 'chat.id', value: '' },
    };

    this.filters.push(filter);
  }

  private addTicketNumberFilter(): void {
    const filter: FilterInterface = {
      text: 'Ticket Number',
      value: 'ticket.ticketNumber',
      icon: 'mdi-pound-box',
      type: FilterType.INPUT,
      items: [],
      selected: { text: 'ticket.ticketNumber', value: '' },
    };

    this.filters.push(filter);
  }

  private addStatusFilter(): void {
    this.filters.push(
      {
        text: 'Status',
        value: 'feedback.status',
        icon: 'mdi-state-machine',
        type: FilterType.MENU,
        items: [
          { text: 'SENT', value: '0' },
          { text: 'SEEN', value: '1' },
          { text: 'REPLIED', value: '2' },
          { text: 'ANSWERED', value: '3' },
          { text: 'CLOSED', value: '4' },
        ],
        selected: { text: '', value: '' },
      },
    );
  }

  private addDatesFilter(): void {
    const dateFilter: FilterInterface = {
      text: 'Date',
      value: 'feedback.timestamp',
      icon: 'mdi-calendar',
      type: FilterType.MENU,
      items: [
        { text: 'TODAY', value: 'today' },
        { text: 'YESTERDAY', value: 'yesterday' },
        { text: 'LAST 7 DAYS', value: '7 days' },
        { text: 'LAST 30 DAYS', value: '30 days' },
        { text: 'LAST MONTH', value: 'last month' },
        { text: 'CURRENT MONTH', value: 'current month' },
        { text: 'CUSTOM PERIOD', value: 'custom period' },
      ],
      selected: { text: '', value: '' },
    };

    this.filters.push(dateFilter);
  }

  private addRatesFilter(): void {
    const rateFilter: FilterInterface = {
      text: 'Rate',
      value: 'chat.rate',
      icon: 'mdi-thumbs-up-down-outline',
      type: FilterType.MENU,
      items: [
        { text: 'NOT RATED', value: '0' },
        { text: 'RATED BAD', value: '1' },
        { text: 'RATED GOOD', value: '2' },
      ],
      selected: { text: '', value: '' },
    };

    this.filters.push(rateFilter);
  }

  private async addTagsFilter(): Promise<void> {
    if (!this.$store.getters.scopes.includes('qa:tag:read')) {
      return;
    }

    this.tags = await this.tagRepositoryV2.getActiveTags();

    const tagsFilter: FilterInterface = {
      text: 'Tags',
      value: 'tags',
      icon: 'mdi-tag-multiple-outline',
      type: FilterType.COMBOBOX,
      items: this.tags.map((tag) => ({ text: tag.label, value: String(tag.id) })),
      selected: { text: 'tags', value: '' },
    };

    this.filters.push(tagsFilter);
  }

  private async applyFilter(): Promise<void> {
    const filterData: FilterRequestData = {};
    this.filters.forEach((filter) => {
      if (!filter.selected || filter.selected.text === '') {
        return;
      }

      if (filter.value === 'feedback.timestamp') {
        filterData[filter.value] = this.getDateFilterData();
        return;
      }

      if (filter.value === 'tags' && filter.selected.value) {
        filterData[filter.value] = (filter.selected.value as unknown as FilterItemInterface[])
          .map((val) => val.value)
          .join(',');
        return;
      }

      filterData[filter.value] = filter.selected.value;
    });

    this.$emit('filter-feedbacks', filterData);
  }

  private getDateFilterData(): string | undefined {
    const dateFilter = this.filters.find((filter) => filter.value === 'feedback.timestamp' && filter.selected);
    if (!dateFilter || !dateFilter.selected || !dateFilter.selected.value) {
      return undefined;
    }

    const selected = dateFilter.selected.value;

    let startDate: Date = new Date();
    let endDate: Date = new Date();
    let filterApplied = false;

    if (selected === 'today') {
      this.customDates = [];
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();

      filterApplied = true;
    }

    if (selected === 'yesterday') {
      this.customDates = [];
      startDate = new Date();
      startDate.setDate(startDate.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setDate(endDate.getDate() - 1);
      endDate.setHours(23, 59, 59, 59);

      filterApplied = true;
    }

    if (selected.endsWith('days')) {
      this.customDates = [];
      const [count] = selected.split(' ');
      startDate = new Date();
      startDate.setDate(startDate.getDate() - Number(count));
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date();
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'last month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);

      endDate = new Date(date.getFullYear(), date.getMonth(), 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'current month') {
      this.customDates = [];
      const date = new Date();

      startDate = new Date(date.getFullYear(), date.getMonth(), 1);

      endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (selected === 'custom period') {
      if (this.customDates.length < 2) {
        return undefined;
      }

      const [startDateStr, endDateStr] = this.customDates;

      startDate = new Date(startDateStr);
      startDate.setHours(0, 0, 0, 0);

      endDate = new Date(endDateStr);
      endDate.setHours(23, 59, 59, 59);
      filterApplied = true;
    }

    if (!filterApplied) {
      return undefined;
    }

    startDate.setMinutes(startDate.getTimezoneOffset());
    endDate.setMinutes(endDate.getTimezoneOffset());
    return `${this.convertDateToString(startDate)},${this.convertDateToString(endDate)}`;
  }

  private convertDateToString(date: Date): string {
    return date.toLocaleString('sv-se');
  }

  private async removeFilter(filter: FilterInterface, filterIndex: number): Promise<void> {
    this.selectedFilters[filterIndex].selected = { text: '', value: '' };
    this.selectedFilters = this.selectedFilters.filter((f) => f.text !== filter.text);
    await this.applyFilter();
  }

  private getTagColor(tagId: string): string {
    const tag = this.tags.find((tag) => tag.id === Number(tagId));

    if (!tag) {
      return 'gray';
    }

    return tag.color;
  }
}
